import { useCallback, useEffect, useState } from "react";
import {
  deleteEntitlementOverrides,
  getEntitlementOverrides,
  upsertEntitlementOverrides,
} from "../../services/api/billingApi";

const NUMERIC_LIMITS = [
  { key: "maxContacts", label: "Contatos" },
  { key: "maxOpportunities", label: "Oportunidades" },
  { key: "maxPipelines", label: "Funis" },
  { key: "maxStagesPerPipeline", label: "Etapas por funil" },
  { key: "maxTeamMembers", label: "Membros da equipe" },
  { key: "maxAutomations", label: "Automações" },
  { key: "maxConnections", label: "Conexões" },
  { key: "maxWebhookIntegrations", label: "Integrações webhook" },
  { key: "maxApiTokens", label: "Tokens de API" },
  { key: "maxForms", label: "Formulários" },
  { key: "maxMonthlyEmails", label: "E-mails por mês" },
];

const FLAG_LIMITS = [
  { key: "apiAccess", label: "Acesso à API" },
  { key: "businessDashboards", label: "Dashboards de negócio" },
  { key: "whatsappBulkCampaigns", label: "Disparo de WhatsApp" },
  { key: "emailBulkCampaigns", label: "Disparo de e-mail" },
];

function formatLimit(value) {
  if (value === undefined || value === null) return "—";
  if (typeof value === "boolean") return value ? "Sim" : "Não";
  if (Number(value) === -1) return "Ilimitado";
  return Number(value).toLocaleString("pt-BR");
}

/** Rascunho do formulário a partir dos ajustes salvos: "" = padrão do plano. */
function draftFromOverrides(overrides) {
  const draft = {};
  NUMERIC_LIMITS.forEach(({ key }) => {
    const v = overrides?.[key];
    draft[key] = v === null || v === undefined ? "" : String(v);
  });
  FLAG_LIMITS.forEach(({ key }) => {
    const v = overrides?.[key];
    draft[key] = v === true ? "true" : v === false ? "false" : "";
  });
  return draft;
}

/** Só envia o que mudou; campo limpo vai como null (volta ao padrão do plano). */
function buildPatch(draft, overrides) {
  const patch = {};
  NUMERIC_LIMITS.forEach(({ key }) => {
    const raw = draft[key].trim();
    const next = raw === "" ? null : Number.parseInt(raw, 10);
    const prev = overrides?.[key] ?? null;
    if (next !== prev) patch[key] = next;
  });
  FLAG_LIMITS.forEach(({ key }) => {
    const next = draft[key] === "" ? null : draft[key] === "true";
    const prev = overrides?.[key] ?? null;
    if (next !== prev) patch[key] = next;
  });
  return patch;
}

function validateDraft(draft) {
  for (const { key, label } of NUMERIC_LIMITS) {
    const raw = draft[key].trim();
    if (raw === "") continue;
    const n = Number(raw);
    if (!Number.isInteger(n) || n < -1) {
      return `"${label}" deve ser um número inteiro (0 ou mais, ou -1 para ilimitado).`;
    }
  }
  return null;
}

export function CompanyLimitsEditor({ company }) {
  const [preview, setPreview] = useState(null);
  const [draft, setDraft] = useState(null);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getEntitlementOverrides(company.id);
      setPreview(data);
      setDraft(draftFromOverrides(data?.overrides));
      setReason(data?.overrides?.reason || "");
    } catch (e) {
      setError(e?.message || "Não foi possível carregar os limites.");
    } finally {
      setLoading(false);
    }
  }, [company.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async () => {
    const validation = validateDraft(draft);
    if (validation) {
      setError(validation);
      setMessage("");
      return;
    }
    const patch = buildPatch(draft, preview?.overrides);
    if (!Object.keys(patch).length) {
      setMessage("Nenhuma alteração para salvar.");
      setError("");
      return;
    }
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await upsertEntitlementOverrides(company.id, patch, reason);
      await load();
      setMessage(`Limites de "${company.name}" atualizados.`);
    } catch (e) {
      setError(e?.message || "Não foi possível salvar os limites.");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(`Remover todos os ajustes de "${company.name}" e voltar aos limites do plano?`)) {
      return;
    }
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await deleteEntitlementOverrides(company.id);
      await load();
      setMessage(`Ajustes de "${company.name}" removidos. Valem os limites do plano.`);
    } catch (e) {
      setError(e?.message || "Não foi possível remover os ajustes.");
    } finally {
      setSaving(false);
    }
  };

  if (loading && !preview) {
    return <p className="muted-text">Carregando limites...</p>;
  }

  const base = preview?.baseLimits || {};
  const effective = preview?.effectiveLimits || {};
  const hasOverrides = Boolean(preview?.hasCustomCatalog);

  return (
    <div className="limits-editor">
      <div className="limits-editor__header">
        <strong>Limites de {company.name}</strong>
        <span className="muted-text">
          Plano base: {preview?.planCode || "—"}. Deixe em branco para usar o padrão do plano; -1 = ilimitado.
        </span>
      </div>

      {message ? <div className="success-box">{message}</div> : null}
      {error ? <div className="error-box">{error}</div> : null}

      {draft ? (
        <table className="data-table limits-editor__table">
          <thead>
            <tr>
              <th>Módulo</th>
              <th>Padrão do plano</th>
              <th>Em vigor</th>
              <th>Ajuste da empresa</th>
            </tr>
          </thead>
          <tbody>
            {NUMERIC_LIMITS.map(({ key, label }) => (
              <tr key={key}>
                <td>{label}</td>
                <td>{formatLimit(base[key])}</td>
                <td>
                  <strong>{formatLimit(effective[key])}</strong>
                </td>
                <td>
                  <input
                    className="table-input table-input--wide"
                    type="number"
                    min={-1}
                    placeholder="Padrão"
                    value={draft[key]}
                    onChange={(e) => setDraft((prev) => ({ ...prev, [key]: e.target.value }))}
                    disabled={saving}
                  />
                </td>
              </tr>
            ))}
            {FLAG_LIMITS.map(({ key, label }) => (
              <tr key={key}>
                <td>{label}</td>
                <td>{formatLimit(base[key])}</td>
                <td>
                  <strong>{formatLimit(effective[key])}</strong>
                </td>
                <td>
                  <select
                    className="table-input table-input--wide"
                    value={draft[key]}
                    onChange={(e) => setDraft((prev) => ({ ...prev, [key]: e.target.value }))}
                    disabled={saving}
                  >
                    <option value="">Padrão</option>
                    <option value="true">Sim</option>
                    <option value="false">Não</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      <div className="limits-editor__footer">
        <input
          className="search-input"
          type="text"
          placeholder="Motivo do ajuste (opcional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={saving}
        />
        <button className="primary-button" type="button" onClick={() => void handleSave()} disabled={saving || !draft}>
          {saving ? "Salvando..." : "Salvar limites"}
        </button>
        {hasOverrides ? (
          <button className="secondary-button" type="button" onClick={() => void handleReset()} disabled={saving}>
            Voltar ao padrão do plano
          </button>
        ) : null}
      </div>
    </div>
  );
}
